import React, { useRef, useState } from 'react';
import { Megaphone, Plus, Trash, X, PaperPlaneTilt, Paperclip, Image, FileText } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AnnouncementsWidget = ({ wrapStyle, announcements, language, isAdmin, onCreateClick, onDelete }) => (
  <div style={wrapStyle}>
    <div className="card" data-testid="announcements-widget">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Megaphone size={20} weight="fill" style={{ color: '#1565C0' }} />
          {language === 'en' ? 'Team Announcements' : 'Annonces equipe'}
        </h3>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={onCreateClick} data-testid="create-announcement-btn">
            <Plus size={14} /> Publier
          </button>
        )}
      </div>
      <div style={{ padding: '12px' }}>
        {announcements.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#9E9E9E', padding: '16px', fontSize: '0.85rem' }}>
            {language === 'en' ? 'No announcements' : 'Aucune annonce'}
          </p>
        ) : announcements.slice(0, 5).map((ann) => {
          const typeColors = { info: '#1565C0', event: '#6A1B9A', urgent: '#C62828' };
          const typeLabels = { info: 'Info', event: 'Evenement', urgent: 'Urgent' };
          const c = typeColors[ann.type] || '#1565C0';
          return (
            <div key={ann.id} data-testid={`announcement-${ann.id}`} style={{ padding: '12px', borderBottom: '1px solid #F4F5F7', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ width: '4px', borderRadius: '2px', backgroundColor: c, alignSelf: 'stretch', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>{ann.title}</span>
                  <span style={{ fontSize: '0.68rem', color: c, fontWeight: '700', textTransform: 'uppercase', backgroundColor: `${c}10`, padding: '2px 8px', borderRadius: '4px' }}>{typeLabels[ann.type] || ann.type}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#37474F', marginBottom: '4px' }}>{ann.content}</div>
                {ann.file_url && (
                  <div style={{ marginTop: '6px', marginBottom: '4px' }}>
                    {ann.file_type === 'image' ? (
                      <img
                        src={`${API_URL}${ann.file_url}?t=${ann.id}`}
                        alt={ann.file_name}
                        onError={(e) => { e.currentTarget.outerHTML = `<span style="display:inline-flex;align-items:center;gap:6px;font-size:0.8rem;color:#C62828;">Image indisponible (${ann.file_name || ''})</span>`; }}
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', objectFit: 'cover' }}
                      />
                    ) : (
                      <a href={`${API_URL}${ann.file_url}?t=${ann.id}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#F4F5F7', borderRadius: '6px', fontSize: '0.8rem', color: '#1565C0', textDecoration: 'none' }}>
                        <FileText size={14} /> {ann.file_name || 'piece jointe'}
                      </a>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#9E9E9E' }}>
                  <span>{ann.author_name} - {ann.created_at?.substring(0, 10)}</span>
                  {ann.event_date && <span style={{ color: '#6A1B9A' }}>RDV: {ann.event_date}</span>}
                </div>
                {ann.recipient_names?.length > 0 && (
                  <div style={{ fontSize: '0.7rem', color: '#5E5F66', marginTop: '2px' }}>Destinataires: {ann.recipient_names.join(', ')}</div>
                )}
              </div>
              {isAdmin && (
                <button onClick={() => onDelete(ann.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', flexShrink: 0 }}><Trash size={14} /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

export const AnnouncementModal = ({ show, onClose, onSubmit, onRefresh, annForm, setAnnForm, allEmployees }) => {
  const fileRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  if (!show) return null;

  const handleSubmitWithFile = async (e) => {
    e.preventDefault();
    if (selectedFile) {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('title', annForm.title);
      fd.append('content', annForm.content);
      fd.append('ann_type', annForm.type);
      fd.append('event_date', annForm.event_date || '');
      fd.append('recipients', (annForm.recipients || []).join(','));
      
      try {
        const axios = (await import('axios')).default;
        await axios.post(`${API_URL}/api/announcements/with-file`, fd, { withCredentials: true });
        setSelectedFile(null);
        onClose();
        if (onRefresh) onRefresh();
      } catch (err) {
        const { toast } = await import('sonner');
        toast.error('Erreur publication');
      }
    } else {
      onSubmit(e);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '520px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Publier une annonce</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmitWithFile} style={{ padding: '20px' }}>
          <div className="form-group"><label className="form-label">Titre *</label><input type="text" className="form-input" value={annForm.title} onChange={(e) => setAnnForm({...annForm, title: e.target.value})} required data-testid="ann-title" /></div>
          <div className="form-group"><label className="form-label">Contenu *</label><textarea className="form-input" rows={3} value={annForm.content} onChange={(e) => setAnnForm({...annForm, content: e.target.value})} required placeholder="Message a diffuser..." data-testid="ann-content" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-input" value={annForm.type} onChange={(e) => setAnnForm({...annForm, type: e.target.value})} data-testid="ann-type">
                <option value="info">Information</option>
                <option value="event">Evenement / RDV</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Date evenement</label><input type="date" className="form-input" value={annForm.event_date} onChange={(e) => setAnnForm({...annForm, event_date: e.target.value})} /></div>
          </div>
          
          {/* File attachment */}
          <div className="form-group">
            <label className="form-label">Piece jointe (photo ou document)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} data-testid="ann-attach-file" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Paperclip size={14} /> Joindre un fichier
              </button>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xlsx" style={{ display: 'none' }} onChange={(e) => setSelectedFile(e.target.files[0] || null)} />
              {selectedFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#E3F2FD', borderRadius: '4px', fontSize: '0.8rem' }}>
                  {selectedFile.type?.startsWith('image/') ? <Image size={14} /> : <FileText size={14} />}
                  <span>{selectedFile.name}</span>
                  <button type="button" onClick={() => setSelectedFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', padding: 0 }}><X size={12} /></button>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Destinataires (vide = tous)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '8px', border: '1px solid #E2E2E6', borderRadius: '6px', maxHeight: '150px', overflowY: 'auto' }}>
              {allEmployees.map(emp => (
                <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', backgroundColor: annForm.recipients.includes(emp.id) ? '#E3F2FD' : 'transparent' }}>
                  <input type="checkbox" checked={annForm.recipients.includes(emp.id)} onChange={(e) => {
                    setAnnForm(prev => ({...prev, recipients: e.target.checked ? [...prev.recipients, emp.id] : prev.recipients.filter(r => r !== emp.id) }));
                  }} style={{ width: '16px', height: '16px' }} />
                  {emp.name}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-announcement"><PaperPlaneTilt size={14} /> Publier</button>
          </div>
        </form>
      </div>
    </div>
  );
};
